// Form Component to Add or Edit status category

import { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { FormRow } from "../../../../components/form/form-row";
import { TProjectCategory } from "../../../../types/config";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  AddEditConfigGroup,
  AddEditStatus,
  GetSpecificConfigGroup,
  GetSpecificStatus,
} from "../../../../utils/api.constant";
import TextArea from "antd/es/input/TextArea";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type TDrawerProps = {
  groupUniqueId: string;
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * Form Component to Add or Edit Config Group
 *
 * @param {TDrawerProps} props - drawer props
 * @returns {JSX.Element}
 */
export default function StatusCategoryForm(props: TDrawerProps) {
  const [form] = Form.useForm(); // Form instance
  const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state
  const { post } = useFetch(); // Hook to make API calls
  const { onShowToast } = useToast(); // Hook to show toast

  // Hook to handle on load operations
  useEffect(() => {
    fetchSpecificGroup(); // Fetch specific group
  }, []);

  // default values initial state
  const defaultValues: TProjectCategory = {
    id: 0,
    name: "",
    description: "",
  };

  // Handle on submit event
  const onFinish = async (values: TProjectCategory) => {
    try {
      const payload = {
        data: {
          ...values,
          id: props.id ? props.id : 0,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditStatus, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const fetchSpecificGroup = async () => {
    if (props.id) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificStatus, payload);
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const { data } = response;
          form.setFieldsValue(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          {/* <Row gutter={16}>
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Status`}
                </h2>
              </Col>
            </Space>
          </Row> */}
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="name"
                label="Status Category"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter status category"),
                  },
                ]}
              >
                <Input placeholder="Enter status category" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="description"
                label="Description"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter description"),
                  },
                ]}
              >
                <TextArea placeholder="Enter description" />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <Button
              type="default"
              disabled={isLoading}
              className="btnDimensions"
              onClick={props.onCloseDrawer}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="btnDimensions"
              onClick={form.submit}
              disabled={isLoading}
            >
              Save
            </Button>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
